import React, { useState, useEffect, useCallback } from 'react';

// ---- import brand assets (in src/assets) ----
import abbLogo from '../assets/abb-logo.png';
import atlasLogo from '../assets/Atlas-Copco-Logo.svg';
import rijkLogo from '../assets/Rijk_Zwaan_Logo.svg';
import youbeepLogo from '../assets/youbeep-2.png';

/* -------------------------------------------
   Helpers: tone → background gradient classes
-------------------------------------------- */
const toneBg = {
  red: 'from-rose-100 via-white to-rose-50',
  teal: 'from-teal-100 via-white to-teal-50',
  green: 'from-lime-100 via-white to-lime-50',
  violet: 'from-violet-100 via-white to-violet-50',
  orange: 'from-orange-100 via-white to-orange-50',
  gray: 'from-gray-100 via-white to-gray-50',
  indigo: 'from-indigo-100 via-white to-indigo-50',
  blue: 'from-blue-100 via-white to-blue-50',
};

/* -------------------------------------------
   Media header (logo mode vs screenshot mode)
-------------------------------------------- */
const ProjectMedia = ({ src, alt, isLogo = false, tone = 'gray' }) => {
  return (
    <div className="relative w-full overflow-hidden rounded-t-2xl">
      <div className={`aspect-[16/9] w-full bg-gradient-to-br ${toneBg[tone]}`}>
        {/* subtle gloss */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.6),transparent_60%)]" />
        {src ? (
          isLogo ? (
            <div className="flex h-full w-full items-center justify-center p-8">
              <img
                src={src}
                alt={alt}
                loading="lazy"
                className="max-h-24 w-auto object-contain md:max-h-28"
              />
            </div>
          ) : (
            <img
              src={src}
              alt={alt}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-gray-500">No cover</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------
   Tiny UI atoms
-------------------------------------------- */
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
    {children}
  </span>
);

/* -------------------------------------------
   Project card
-------------------------------------------- */
const ProjectCard = ({ p, onOpen }) => {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 transition hover:shadow-md">
      <ProjectMedia
        src={p.image}
        alt={`${p.client} logo`}
        isLogo={p.imageIsLogo}
        tone={p.tone}
      />

      <div className="flex flex-1 flex-col p-5">
        <header>
          <h3 className="text-base font-semibold text-gray-900">{p.client}</h3>
          <p className="text-sm text-gray-600">{p.name}</p>
          <p className="mt-1 text-xs text-gray-500">
            {p.role} • {p.period}
          </p>
        </header>

        <p className="mt-3 line-clamp-3 text-sm text-gray-700">{p.summary}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {p.tech.map((t) => (
            <Badge key={t}>{t}</Badge>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => onOpen(p)}
            className="rounded-xl bg-teal-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            View case study
          </button>
          {p.links?.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </article>
  );
};

/* -------------------------------------------
   Enriched project data (updated ABB copy + Intershop pills)
-------------------------------------------- */
const projects = [
  {
    id: 'abb',
    tone: 'red',
    image: abbLogo,
    imageIsLogo: true,
    name: 'Robotics One Portal',
    client: 'ABB Robotics',
    role: 'Senior Frontend Developer',
    period: '2023—Present',
    summary:
      'Senior frontend role on Robotics One, leading critical platform delivery across product readiness, analytics, and quality. I owned end-to-end Medallia integration (design, implementation, documentation, and demos), contributed to SSR/performance work, and helped drive a successful production go-live while aligning engineering, QA, and client stakeholders.',
    responsibilities: [
      'Owned Medallia integration lifecycle: technical design, implementation, docs, and live demos',
      'Supported production readiness and go-live delivery for ABB Robotics One portal',
      'Contributed to SSR and performance optimization initiatives, including memory-leak fixes',
      'Implemented and maintained core flows: SSO/authentication, search, consent, and analytics',
      'Acted as communication bridge between developers, QA, and client stakeholders',
      'Raised engineering quality standards around unit tests, code health, and maintainability',
      'Delivered resilient UI patterns with strong accessibility and fallback behavior',
    ],
    highlights: [
      {
        label: 'Delivery',
        value:
          'Led high-impact features required for production go-live and post-launch stability',
      },
      {
        label: 'Ownership',
        value:
          'End-to-end ownership in Medallia integration and cross-team technical alignment',
      },
      {
        label: 'Quality',
        value:
          'Improved platform quality through performance work, analytics observability, and test advocacy',
      },
    ],
    tech: [
      'Angular',
      'Spartacus',
      'NgRx',
      'Keycloak',
      'Coveo',
      'Medallia',
      'A11y',
      'CI/CD',
    ],
    links: [{ label: 'Public site', href: 'https://one.robotics.abb.com/en/' }],
  },
  {
    id: 'atlas',
    tone: 'blue',
    image: atlasLogo,
    imageIsLogo: true,
    name: 'Component Library & Perf Hardening',
    client: 'Atlas Copco',
    role: 'Frontend Developer',
    period: '2022—2023',
    summary:
      'Built slider filters using only HTML + SCSS, refactored Angular patterns, documented components, and tightened CI for reliable releases.',
    responsibilities: [
      'HTML/CSS-only slider filters (no JS) with accessible semantics',
      'OnPush and selective signals adoption',
      'Route/asset lazy loading & hydration tweaks',
      'Storybook coverage + visual regression tests',
      'Grid & multi-line clamp utilities / safe truncation',
    ],
    highlights: [
      { label: 'DX', value: 'Faster onboarding via docs + Storybook' },
      { label: 'Perf', value: 'Add concrete LCP/TTFB improvements' },
    ],
    tech: [
      'Angular',
      'Intershop',
      'NgRx',
      'Storybook',
      'HTML',
      'SCSS',
      'Perf',
      'A11y',
      'CI/CD',
    ],
    links: [],
  },
  {
    id: 'rijk',
    tone: 'green',
    image: rijkLogo,
    imageIsLogo: true,
    name: 'E-commerce Storefront & 100-Year Redesign',
    client: 'Rijk Zwaan',
    role: 'Frontend Developer',
    period: '2022—2023',
    summary:
      'Participated in Contentful integration and supported the brand’s 100-year anniversary redesign: complex shapes, news/article pages, PDP templates and more.',
    responsibilities: [
      'Contentful integration & content migration/release',
      'Complex design shapes and layout polish across the storefront',
      'News/article pages & PDP templates implementation',
      'Telemetry hooks for GA/GTM + error toasts',
      'Unit testing and CI/CD pipelines for safer releases',
      'Release checklist + rollbacks; hotfixes and support for post-launch issues',
    ],
    highlights: [
      { label: 'Brand', value: '100-year anniversary redesign support' },
      { label: 'Reliability', value: 'Safer releases and faster triage' },
    ],
    tech: ['Angular', 'Intershop', 'NgRx', 'Contentful', 'GA/GTM', 'CI/CD'],
    links: [{ label: 'Public site', href: 'https://rijkzwaan.com/en/home' }],
  },
  {
    id: 'youbeep',
    tone: 'teal',
    image: youbeepLogo,
    imageIsLogo: true,
    name: 'YouBeep Mobile App (Hybrid)',
    client: 'Xhockware / YouBeep',
    role: 'Software Developer',
    period: '2017—2018',
    summary:
      'Vue + Apache Cordova (Framework7) hybrid checkout app. Contributed to a major rework focused on maintainability, performance and multi-device support.',
    responsibilities: [
      'Refactored legacy views into reusable Vue components',
      'Improved state & navigation patterns for reliability',
      'Performance tuning for low-end devices (bundle size, lazy views)',
      'Theming & responsive layouts across various screen sizes',
      'Bug triage, regression tests and release support',
    ],
    highlights: [
      {
        label: 'Maintainability',
        value: 'Simplified code paths and reduced duplication',
      },
      { label: 'Perf', value: 'Smoother flows on constrained devices' },
    ],
    tech: ['Vue', 'Cordova', 'Framework7', 'HTML', 'CSS', 'Unit Tests'],
    links: [
      { label: 'YouBeep', href: 'https://www.youbeep.com/' },
      {
        label: 'Xhockware • YouBeep',
        href: 'https://www.xhockware.com/youbeep/',
      },
    ],
  },
];

/* -------------------------------------------
   Page
-------------------------------------------- */
const ProjectsPage = () => {
  const [open, setOpen] = useState(null);

  const closeModal = useCallback(() => setOpen(null), []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeModal]);

  return (
    <div className="min-h-screen bg-orange-200 py-16 px-6" id="projects">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-6 flex items-center gap-2 text-3xl font-bold text-black">
          <span
            className="inline-block h-3 w-3 rounded-full bg-teal-600"
            aria-hidden="true"
          />
          Projects
        </h1>

        <div className="grid gap-6 sm:grid-cols-2">
          {projects.map((p) => (
            <ProjectCard key={p.id} p={p} onOpen={setOpen} />
          ))}
        </div>

        {/* Modal */}
        {open && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${open.id}-title`}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
            onClick={closeModal}
          >
            <div
              className="mt-10 w-full max-w-3xl rounded-2xl bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 border-b p-5">
                <div>
                  <h3
                    id={`${open.id}-title`}
                    className="text-lg font-semibold text-gray-900"
                  >
                    {open.client}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {open.name} • {open.role} • {open.period}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="rounded-full p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-600"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6 p-5">
                <p className="text-sm text-gray-800">{open.summary}</p>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Key Responsibilities
                    </h4>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                      {open.responsibilities.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Highlights
                    </h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                      {open.highlights.map((h, i) => (
                        <li key={i}>
                          <strong>{h.label}:</strong> {h.value}
                        </li>
                      ))}
                    </ul>

                    <h4 className="mt-4 text-sm font-semibold text-gray-900">
                      Stack
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {open.tech.map((t) => (
                        <Badge key={t}>{t}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {open.links?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {open.links.map((l) => (
                      <a
                        key={l.href}
                        href={l.href}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
